import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../../components/Editorial/useReveal';
import BookCallModal from '../../components/Editorial/BookCallModal';
import { NGO_PARTNERS } from '../../data/ngoPartners';
import '../../styles/editorial.css';

export default function EditorialHome() {
  useReveal();
  const [showBookCallModal, setShowBookCallModal] = useState(false);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const halfNgoCount = Math.ceil(NGO_PARTNERS.length / 2);
  const ngoRow1 = NGO_PARTNERS.slice(0, halfNgoCount);
  const ngoRow2 = NGO_PARTNERS.slice(halfNgoCount);

  const impactStories = [
    {
      name: 'Shrutika, 19',
      location: 'Mitnapur, Yavatmal district, Maharashtra',
      image: '/img/shrutika.jpg',
      quote: '“A laptop a company had retired became the start of her business.”',
      text: 'After her father passed away, Shrutika watched debt weigh on her family. At 19, she set up a Sama Digi Kendra at home on a refurbished laptop that a company had retired. Her centre has since served 900+ people in her village, and in one week in September 2026 she earned ₹10,000. She is now expanding it to serve children and the elderly.'
    },
    {
      name: 'Kavita, 21',
      location: 'Raigarh, Chhattisgarh',
      image: '/img/builder-1.jpg',
      quote: '“Teaching 45 girls digital skills changed the mindset of our entire community.”',
      text: 'Kavita received a refurbished laptop through Sama’s rural empowerment deployment. She now conducts daily batches teaching typing, office spreadsheets, and internet research to young women preparing for banking and civil examinations.'
    },
    {
      name: 'Pooja, 22',
      location: 'Pune, Maharashtra',
      image: '/img/builder-2.jpg',
      quote: '“Without a working machine, freelancing in graphic design was impossible.”',
      text: 'Coming from an underserved background, purchasing high-specification hardware was out of reach. Through Build for Good, Pooja received a refurbished laptop and now supports her household with regular client design contracts.'
    },
    {
      name: 'Sunita, 20',
      location: 'Bilaspur, Chhattisgarh',
      image: '/img/builder-3.jpg',
      quote: '“Villagers no longer have to travel 25 kilometers for basic citizen services.”',
      text: 'Sunita’s Kendra serves as the digital lifeline of her gram panchayat. With a refurbished laptop and biometric device, she assists more than 1,200 residents with DBT schemes, scholarship forms, and government certificates.'
    },
    {
      name: 'Aarti, 18',
      location: 'Yavatmal, Maharashtra',
      image: '/img/builder-4.jpg',
      quote: '“Having my own laptop means I no longer queue for an hour at a cyber café.”',
      text: 'As the first girl in her family to enter higher education, Aarti uses her Sama laptop for online lectures, test preparation, and digital libraries. Her younger siblings share the machine in the evenings for school homework.'
    },
    {
      name: 'Rani, 23',
      location: 'Gondia, Maharashtra',
      image: '/img/builder-5.jpg',
      quote: '“One refurbished laptop unlocked hands-on computer learning for 80 children.”',
      text: 'Rani partnered with her local village school to deliver computer classes after school hours. Students who had never touched a keyboard before are now learning digital literacy and creative computing.'
    }
  ];

  const [stats, setStats] = React.useState({
    laptops: '21,142',
    people: '4,22,840',
    states: '22',
    districts: '68',
    carbon: '5,803+ MT',
    ngos: '89',
    progressPercent: '42%',
  });

  useEffect(() => {
    document.title = "Sama Digital Foundation | A laptop’s second life. Someone’s first.";
    window.scrollTo(0, 0);

    const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:8000' : 'https://sama-api.thesama.in';
    fetch(`${apiBase}/api/public/social-impact-stats`)
      .then(res => res.json())
      .then(data => {
        if (data) {
          const rawLaptops = data.laptopsReceived || 21142;
          // Dynamic calculation: 1 laptop = 20 beneficiaries
          const peopleCount = data.beneficiariesImpacted || (rawLaptops * 20);
          const carbonMt = data.carbonEmissionsAvoided || Math.round((rawLaptops * 274.5) / 1000);
          const pct = Math.min(100, Math.max(1, Math.round((peopleCount / 1000000) * 100)));
          setStats({
            laptops: `${Number(rawLaptops).toLocaleString('en-IN')}`,
            people: `${Number(peopleCount).toLocaleString('en-IN')}`,
            states: `${data.statesImpacted || 22}`,
            districts: `${data.districtsImpacted || 68}`,
            carbon: `${Number(carbonMt).toLocaleString('en-IN')}+ MT`,
            ngos: `${data.ngoPartners || 89}`,
            progressPercent: `${pct}%`,
          });
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="editorial-page page" id="page-home">
{/*  A1 · Tagline + hero carousel + CTAs  */}
<section className="home-hero">
  <div className="wrap grid">
    <div className="reveal">
      <span className="eyebrow eyebrow--light">Sama Digital Foundation</span>
      <h1 className="display mt-m">A laptop’s second life. <em>Someone’s first.</em></h1>
      <div className="btn-row mt-l">
        <Link className="bracket bracket--solid" to="/donate">Donate laptops <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></Link>
        <a className="bracket bracket--light" href="#impact">See our impact <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></a>
      </div>
    </div>
    <div className="carousel reveal" aria-label="Photos of Sama laptops in use">
      <img className="on" src="/img/students-lab.jpg" alt="Young women working on refurbished laptops in a classroom" />
      <img src="/img/college-lab.jpg" alt="College students in a Sama digital lab" />
      <img src="/img/school-handover.jpg" alt="Government school students receiving laptops with their teachers" />
      <img src="/img/bfg-finalists.jpg" alt="Build for Good finalists with their refurbished laptops" />
      <div className="dots"><i className="on"></i><i></i><i></i><i></i></div>
    </div>
  </div>
</section>

{/*  A7 · Impact in numbers  */}
<section className="sec dark" id="impact">
  <div className="wrap">
    <div className="reveal">
      <span className="pill pill--live">Live impact data</span>
      <h2 className="display h2 mt-m">Our impact, <em>in numbers.</em></h2>
    </div>
    <div className="stat-row reveal">
      <div><b>{stats.laptops}</b><span>Laptops received</span></div>
      <div><b>{stats.people}</b><span>People reached</span></div>
      <div><b>{stats.states}</b><span>States reached</span></div>
      <div><b>{stats.districts}</b><span>Districts reached</span></div>
      <div><b>{stats.carbon}</b><span>Carbon emissions avoided</span></div>
      <div><b>{stats.ngos}</b><span>NGO partners</span></div>
    </div>
  </div>
</section>

{/*  A2 · Who we are  */}
<section className="sec">
  <div className="wrap reveal">
    <span className="eyebrow">Who we are</span>
    <p className="statement mt-m">At Sama Digital Foundation, we repurpose retired corporate laptops into a growth and learning tool for underserved women and youth across India. Together with our corporate partners, we turn devices headed for e-waste into <b>access to learning, skills and income</b>, opening up pathways to social mobility.</p>
  </div>
</section>

{/*  A5 · The gap in numbers  */}
<section className="sec">
  <div className="wrap">
    <div className="reveal">
      <span className="eyebrow">The problem</span>
      <h2 className="display h2 mt-s">India has two digital gaps. <em>They meet in the same place.</em></h2>
      <p className="lede mt-m">On one side, organisations retire devices that have reached the end of their corporate life. On the other, schools, colleges, skilling centres and communities need working computers to turn a curriculum into something people can actually sit down and do.</p>
    </div>
    <div className="stat-grid mt-l reveal">
      <div className="stat-card"><b>6.19 lakh</b><p>Schools with no functional computer for teaching: 42% of every school in India</p><small>UDISE+ 2024–25</small></div>
      <div className="stat-card"><b>12.16 crore</b><p>Students enrolled in India’s government schools</p><small>UDISE+ 2024–25</small></div>
      <div className="stat-card"><b>4.2%</b><p>Of rural households own a computer of any kind; 9.9% nationally</p><small>NSS 79th round (CAMS) 2022–23</small></div>
      <div className="stat-card"><b>13.98 lakh MT</b><p>E-waste generated in FY 2024–25; 29% never formally collected or recycled</p><small>CPCB, Rajya Sabha reply, July 2025</small></div>
      <div className="stat-card"><b>274.5 kg</b><p>CO₂e in lifecycle emissions avoided each time one laptop is reused instead of replaced</p><small>Laptop life-cycle assessment, 2025</small></div>
      <div className="stat-card" style={{ background: 'var(--green)', borderColor: 'var(--green)' }}><b style={{ color: '#fff' }}>85% vs 33.9%</b><p style={{ color: '#DDE9E0' }}>Access is what changes the outcome. Among rural 14–18 year-olds surveyed by ASER, 85% of those with a computer at home could use one, against 33.9% of those without.</p><small style={{ color: '#A6CFB0' }}>ASER</small></div>
    </div>
  </div>
</section>

{/*  A6 · How it works  */}
<section className="sec how-band" id="how">
  <div className="wrap">
    <div className="reveal">
      <span className="eyebrow eyebrow--light">How it works</span>
      <h2 className="display h2 mt-s">How a retired laptop gets <em>a second life.</em></h2>
      <p className="lede mt-m">From a company’s storeroom to someone’s hands, every step is checked and recorded.</p>
    </div>
    <div className="zz">
      <div className="zz__phase reveal"><span>Digital resource recovery</span></div>
      <div className="zz__step zz__step--l reveal"><span className="zz__dot"></span><div className="zz__card"><img src="/img/proc-collect.png" alt="" loading="lazy" /><div><span className="n">01</span><h3>Collect</h3><p>We collect end-of-life laptops from our corporate partners.</p></div></div></div>
      <div className="zz__step zz__step--r reveal"><span className="zz__dot"></span><div className="zz__card"><img src="/img/proc-secure.png" alt="" loading="lazy" /><div><span className="n">02</span><h3>Secure data</h3><p>We ensure complete data erasure and privacy protection.</p></div></div></div>
      <div className="zz__step zz__step--l reveal"><span className="zz__dot"></span><div className="zz__card"><img src="/img/proc-restore.png" alt="" loading="lazy" /><div><span className="n">03</span><h3>Restore</h3><p>We restore and upgrade each laptop for learning and work.</p></div></div></div>
      <div className="zz__phase reveal"><span>Digital inclusion pipeline</span></div>
      <div className="zz__step zz__step--r reveal"><span className="zz__dot"></span><div className="zz__card"><img src="/img/proc-livelihood.png" alt="" loading="lazy" /><div><span className="n">04</span><h3>Create opportunity</h3><p>We create livelihood opportunities through our programmes.</p></div></div></div>
      <div className="zz__step zz__step--l reveal"><span className="zz__dot"></span><div className="zz__card"><img src="/img/proc-ecosystem.png" alt="" loading="lazy" /><div><span className="n">05</span><h3>Load learning</h3><p>We provide pre-installed online and offline learning resources.</p></div></div></div>
      <div className="zz__step zz__step--r reveal"><span className="zz__dot"></span><div className="zz__card"><img src="/img/proc-connect.png" alt="" loading="lazy" /><div><span className="n">06</span><h3>Reach communities</h3><p>We reach underserved communities through our network of NGO and government partners.</p></div></div></div>
      <div className="zz__phase reveal"><span>Digital tracking and reporting</span></div>
      <div className="zz__step zz__step--l reveal"><span className="zz__dot"></span><div className="zz__card"><img src="/img/proc-capture.png" alt="" loading="lazy" /><div><span className="n">07</span><h3>Capture data</h3><p>We capture data on each laptop’s lifecycle and community impact.</p></div></div></div>
      <div className="zz__step zz__step--r reveal"><span className="zz__dot"></span><div className="zz__card"><img src="/img/proc-analytics.png" alt="" loading="lazy" /><div><span className="n">08</span><h3>Report &amp; improve</h3><p>We share transparent reporting on ESG outcomes with our partners, and use what we learn to improve how we work.</p></div></div></div>
    </div>
    <div className="custody reveal"><span className="dot"></span><p><b style={{ color: 'var(--paper)' }}>Full chain of custody:</b> every laptop is recorded on our dashboard from pickup to the person who uses it. Corporate laptops are refurbished by our operations team, 90% of whom are women and beneficiaries of Sama Digital Foundation themselves.</p></div>
  </div>
</section>

{/*  For companies · corporate advantages  */}
<section className="sec corp" id="for-companies">
  <div className="wrap">
    <div className="corp__head reveal">
      <div><span className="eyebrow">For companies</span>
        <h2 className="display h2 mt-s">Retire your laptops responsibly. <em>See exactly where they go.</em></h2></div>
      <p className="lede">Your retired laptops still have years of work left in them. We make passing them on simple, secure and reportable.</p>
    </div>
    <div className="adv-grid reveal">
      <div className="adv"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#B25F65" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="7" width="18" height="13" rx="1.5"/><path d="M3 11h18M9 7V4h6v3"/></svg><h3>Handled end to end</h3><p>We collect retired laptops from your offices, refurbish them and deploy them through 60+ NGO partners. Your IT team hands over; we do the rest.</p></div>
      <div className="adv"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#B25F65" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="1.5"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg><h3>Your data stays protected</h3><p>Laptops are wiped as per your IT policy before handover, or the drives are removed before pickup.</p></div>
      <div className="adv"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#B25F65" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 21s-7-6.3-7-11.5A7 7 0 0 1 19 9.5C19 14.7 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.5"/></svg><h3>Full traceability</h3><p>Every laptop is logged on our dashboard from pickup to the person who uses it.</p></div>
      <div className="adv"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#B25F65" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="3" width="14" height="18" rx="1.5"/><path d="M9 8h6M9 12h6M9 16h3"/></svg><h3>Impact you can report</h3><p>Regular updates on laptops refurbished, people reached and where each batch went, ready for your CSR and ESG reporting.</p></div>
      <div className="adv"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#B25F65" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 19c0-8 5-13 14-14 0 9-5 14-13 14"/><path d="M5 19l8-8"/></svg><h3>Measurable sustainability</h3><p>Every laptop reused instead of replaced avoids about 274.5 kg CO₂e of lifecycle emissions. Our partners have avoided {stats.carbon} so far.</p></div>
      <div className="adv"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#B25F65" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="9" cy="8" r="3"/><path d="M3 20c.8-3.5 3.2-5.5 6-5.5s5.2 2 6 5.5"/><circle cx="17" cy="9" r="2.4"/><path d="M16 14.6c2.6.2 4.4 2 5 5.4"/></svg><h3>Employees who take part</h3><p>Bring a <Link to="/giving#renew-desk" style={{ textDecoration: 'underline', fontWeight: 600, color: 'inherit' }}>Renew Desk</Link> to your office, where employees refurbish laptops alongside our team.</p></div>
    </div>
    <div className="corp__foot reveal">
      <span className="pill" style={{ marginTop: '0' }}>★ CSR-1 registered with the Ministry of Corporate Affairs</span>
      <div className="btn-row">
        <Link className="bracket bracket--solid" to="/donate">Donate your retired laptops <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></Link>
        <button type="button" className="bracket bracket--line" onClick={() => setShowBookCallModal(true)}>Book 30 minutes with us <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></button>
      </div>
    </div>
  </div>
</section>

{/*  A8 · Partners Ecosystem  */}
<section className="trust">
  <div className="wrap reveal">
    <div className="center">
      <span className="eyebrow">Partnership Ecosystem</span>
      <h2 className="display h2 mt-s" style={{ fontSize: '36px' }}>Powering digital inclusion <em>across India.</em></h2>
    </div>

    <div className="mt-l">
      <span className="small" style={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: '14px' }}>
        Corporate Fleet &amp; Asset Donors
      </span>
      <div className="marquee" aria-label="Our corporate partners">
        <ul className="marquee__track">
          <li><img src="/img/p-amazon.png" alt="Amazon" loading="lazy" /></li>
          <li><img src="/img/p-microsoft.png" alt="Microsoft" loading="lazy" /></li>
          <li><img src="/img/p-accenture.png" alt="Accenture" loading="lazy" /></li>
          <li><img src="/img/p-capgemini.svg" alt="Capgemini" loading="lazy" /></li>
          <li><img src="/img/p-eaton.png" alt="Eaton" loading="lazy" /></li>
          <li><img src="/img/p-sp-global.png" alt="S&amp;P Global" loading="lazy" /></li>
          <li><img src="/img/p-tiger-analytics.png" alt="Tiger Analytics" loading="lazy" /></li>
          <li><img src="/img/p-macquarie.png" alt="Macquarie" loading="lazy" /></li>
          <li><img src="/img/p-dxc.png" alt="DXC Technology" loading="lazy" /></li>
          <li><img src="/img/p-apexon.png" alt="Apexon" loading="lazy" /></li>
          <li><img src="/img/p-avid.png" alt="Avid Learning" loading="lazy" /></li>
          <li><img src="/img/p-fossil.svg" alt="Fossil" loading="lazy" /></li>
          <li><img src="/img/p-sga.svg" alt="SG Analytics" loading="lazy" /></li>
        </ul>
      </div>
    </div>

    <div className="ngo-partners-section mt-l" style={{ borderTop: '1px solid rgba(69,55,34,0.1)', paddingTop: '40px' }}>
      {/* Centered Structured Header */}
      <div className="ngo-header-block">
        <div className="ngo-header-badge">
          <span className="pill" style={{ background: 'rgba(92,120,90,0.12)', color: 'var(--sage)', borderColor: 'var(--sage)' }}>
            40+ Verified Grassroots Partners
          </span>
        </div>
        <h3 className="ngo-header-title">Implementation &amp; Community Partners</h3>
        <p className="ngo-header-subtitle">
          Refurbished corporate laptops deployed across grassroots classrooms, residential skilling campuses, and women-led Digi Kendras across India.
        </p>
      </div>

      {/* 2-Row Horizontal Scrolling Logo Showcase */}
      <div className="ngo-marquee-wrap" aria-label="Implementation Partner Logos">
        {/* Row 1 - Smooth Left Scroll */}
        <div className="ngo-marquee-row ngo-marquee-row--left">
          <ul className="ngo-marquee-track">
            {ngoRow1.concat(ngoRow1).map((p, idx) => {
              const TileInner = (
                <>
                  <img
                    src={p.logo}
                    alt={p.name}
                    loading="eager"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      if (e.currentTarget.nextElementSibling) {
                        e.currentTarget.nextElementSibling.style.display = 'block';
                      }
                    }}
                  />
                  <span
                    className="ngo-logo-fallback"
                    style={{ display: 'none', fontSize: '12px', fontWeight: 600, color: 'var(--ink)', textAlign: 'center' }}
                  >
                    {p.name}
                  </span>
                </>
              );

              return (
                <li
                  key={`r1-${p.id}-${idx}`}
                  className="ngo-logo-tile"
                  title={`${p.name}${p.sub ? ` · ${p.sub}` : ''}`}
                >
                  {p.website ? (
                    <a
                      href={p.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ngo-logo-link"
                      aria-label={p.name}
                    >
                      {TileInner}
                    </a>
                  ) : (
                    TileInner
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Row 2 - Smooth Right (Reverse) Scroll */}
        <div className="ngo-marquee-row ngo-marquee-row--right">
          <ul className="ngo-marquee-track">
            {ngoRow2.concat(ngoRow2).map((p, idx) => {
              const TileInner = (
                <>
                  <img
                    src={p.logo}
                    alt={p.name}
                    loading="eager"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      if (e.currentTarget.nextElementSibling) {
                        e.currentTarget.nextElementSibling.style.display = 'block';
                      }
                    }}
                  />
                  <span
                    className="ngo-logo-fallback"
                    style={{ display: 'none', fontSize: '12px', fontWeight: 600, color: 'var(--ink)', textAlign: 'center' }}
                  >
                    {p.name}
                  </span>
                </>
              );

              return (
                <li
                  key={`r2-${p.id}-${idx}`}
                  className="ngo-logo-tile"
                  title={`${p.name}${p.sub ? ` · ${p.sub}` : ''}`}
                >
                  {p.website ? (
                    <a
                      href={p.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ngo-logo-link"
                      aria-label={p.name}
                    >
                      {TileInner}
                    </a>
                  ) : (
                    TileInner
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Centered Structured Trust Bar */}
      <div className="ngo-trust-bar">
        <span className="pill" style={{ background: 'rgba(69,55,34,0.06)', color: 'var(--ink)', border: '1px solid var(--line)' }}>
          ★ CSR-1 registered with the Ministry of Corporate Affairs
        </span>
        <span className="small" style={{ color: 'var(--muted)', fontSize: '13.5px' }}>
          Working alongside 60+ institutional, corporate and grassroots partners across 22 States &amp; 68 Districts.
        </span>
      </div>
    </div>
  </div>
</section>

{/*  People behind the numbers (collage)  */}
<section className="faces">
  <div className="wrap">
    <div className="faces__head reveal">
      <div><span className="eyebrow eyebrow--light">The people behind the numbers</span>
      <h2 className="display h2 mt-s">{stats.people} people. <em>Every one of them has a name.</em></h2></div>
      <p className="lede">Each face here is a girl or young woman learning, studying or working on a laptop that a company once retired.</p>
    </div>
    <figure className="faces__img reveal">
      <img src="/img/faces.jpg" alt="A collage of young women and girls across India holding and working on their refurbished laptops" />
    </figure>
  </div>
</section>

{/*  A4 · Voices of Impact (Interactive Multi-story Carousel)  */}
<section className="sec dark" id="stories">
  <div className="wrap">
    <div className="reveal" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
      <div>
        <span className="pill pill--live">Voices of Impact</span>
        <h2 className="display h2 mt-s" style={{ color: 'var(--paper)' }}>Stories from <em>the field.</em></h2>
        <p className="lede mt-s" style={{ color: 'rgba(245,242,237,.78)' }}>See how a retired corporate laptop opens up income, education, and social mobility across India.</p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button
          type="button"
          onClick={() => setCurrentStoryIndex((prev) => (prev === 0 ? impactStories.length - 1 : prev - 1))}
          className="bracket bracket--light"
          style={{ padding: '8px 14px', cursor: 'pointer' }}
          aria-label="Previous story"
        >
          ← Prev
        </button>
        <span style={{ fontSize: '13px', color: 'rgba(245,242,237,.75)', minWidth: '45px', textAlign: 'center' }}>
          {currentStoryIndex + 1} / {impactStories.length}
        </span>
        <button
          type="button"
          onClick={() => setCurrentStoryIndex((prev) => (prev === impactStories.length - 1 ? 0 : prev + 1))}
          className="bracket bracket--light"
          style={{ padding: '8px 14px', cursor: 'pointer' }}
          aria-label="Next story"
        >
          Next →
        </button>
      </div>
    </div>

    <div className="story-feature mt-m" key={currentStoryIndex} style={{ animation: 'samafadein 0.35s ease', opacity: 1 }}>
      <img
        src={impactStories[currentStoryIndex].image}
        alt={impactStories[currentStoryIndex].name}
        style={{ width: '100%', maxHeight: '420px', objectFit: 'cover', borderRadius: '12px' }}
      />
      <div className="body">
        <blockquote>{impactStories[currentStoryIndex].quote}</blockquote>
        <div className="who">
          <b>{impactStories[currentStoryIndex].name}</b> <span>· {impactStories[currentStoryIndex].location}</span>
        </div>
        <p>{impactStories[currentStoryIndex].text}</p>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap', marginTop: '20px' }}>
          <Link className="text-link" style={{ color: 'var(--ink)' }} to="/sama-saathi">
            Explore Sama Saathi programme <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </Link>
          <Link className="text-link" style={{ color: 'var(--ink)' }} to="/donate">
            Fund a laptop for a learner <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </Link>
        </div>
      </div>
    </div>

    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
      {impactStories.map((_, idx) => (
        <button
          key={idx}
          type="button"
          onClick={() => setCurrentStoryIndex(idx)}
          style={{
            width: idx === currentStoryIndex ? '28px' : '9px',
            height: '9px',
            borderRadius: '5px',
            background: idx === currentStoryIndex ? 'var(--sage)' : 'rgba(255,255,255,0.25)',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.25s ease'
          }}
          aria-label={`Jump to story ${idx + 1}`}
        />
      ))}
    </div>
  </div>
</section>

{/*  A3 · What drives us  */}
<section className="sec bg-stone">
  <div className="wrap split">
    <div className="media reveal"><img src="/img/students-lab.jpg" alt="Young women learning on refurbished laptops" /></div>
    <div className="reveal">
      <span className="eyebrow">What drives us</span>
      <h2 className="display h2 mt-s">A computer is no longer <em>optional.</em></h2>
      <p className="lede mt-m">For a first-time learner in India, it is the classroom, the bank, the job application and the shop counter. Sama makes sure that not having a device is never what holds back an underserved woman or young person.</p>
      <div className="mv mv--vision"><span className="eyebrow">Vision</span><p>Everyone in India who has never used a computer, using one with confidence.</p></div>
    </div>
  </div>
  <div className="wrap mt-xl reveal"><div className="video"><iframe src="https://drive.google.com/file/d/1KUbE0jDX03YPiVaZJbb7xb4twuJgeEwu/preview" title="Sama Digital Foundation film" allow="autoplay; fullscreen" loading="lazy"></iframe></div></div>
</section>

{/*  A9 · Vision for 2030  */}
<section className="sec sage">
  <div className="wrap">
    <div className="reveal">
      <span className="eyebrow" style={{ color: '#DDE7DA' }}>Our vision for 2030</span>
      <h2 className="display h1 mt-s">1 million <em>by 2030.</em></h2>
      <p className="lede mt-m" style={{ color: 'rgba(255,255,255,.88)' }}>By 2030, we aim to reach 1 million women and young people across India. We take laptops that companies no longer need, refurbish them, and give them to people who will use them to study and earn. Every laptop we repurpose stays out of a landfill and helps someone become more independent.</p>
    </div>
    <div className="progress reveal">
      <div className="top"><div className="now"><b>{stats.people}</b><span>people reached</span></div><div className="goal"><b>1M</b><span>goal by 2030</span></div></div>
      <div className="track"><i data-w={stats.progressPercent} style={{ width: stats.progressPercent }}></i></div>
      <div className="ticks"><span>0</span><span>2.5 Lakh</span><span>5 Lakh</span><span>7.5 Lakh</span><span>10 Lakh</span></div>
    </div>
    <div className="btn-row mt-l reveal"><Link className="bracket bracket--paper" to="/donate">Donate laptops <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></Link></div>
  </div>
</section>

{/*  Reports band  */}
<section className="report-band">
  <div className="wrap grid reveal">
    <div><span className="eyebrow">Our reports</span><h2 className="display h2 mt-s">Read our work, <em>year by year.</em></h2></div>
    <div className="report-links">
      <Link className="report-chip" to="/communications#reports"><b>Annual Report</b><span>FY 2025–26</span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></Link>
      <Link className="report-chip" to="/communications#reports"><b>Impact Report</b><span>FY 2024–25</span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></Link>
    </div>
  </div>
</section>

      {/* Book a Call Modal */}
      <BookCallModal
        isOpen={showBookCallModal}
        onClose={() => setShowBookCallModal(false)}
        initialTopic="Renew Desk at our office"
      />
    </div>
  );
}
